// @flow
import { useEffect, useState } from 'react';
import Debug from 'debug';
import OlFeature from 'ol/Feature';
import type { Feature as FeatureType, Geometry, Style, Properties, Layer, Source, TranslateEvent } from '../types';
import { useAddFeatureListener, useRemoveFeatureListener } from '../context';
import { useLayer } from '../layers/context';

const debug = Debug('amms:modules:openlayers:feature');
const rtDebug = Debug('rt:amms:modules:openlayers:feature');

type Props = $ReadOnly<{
    geometry: Geometry,
    style: Style,
    properties: Properties,
    layer?: Layer,
    source?: Source,

    onSelect?: () => void,
    onDeselect?: () => void,
    onDragStart?: (e: TranslateEvent) => void,
    onDrag?: (e: TranslateEvent) => void,
    onDragEnd?: (e: TranslateEvent) => void
}>;

function Feature({ geometry, style, properties, layer, source, onSelect, onDeselect, onDragStart, onDrag, onDragEnd }: Props) {
    const [feature] = useState<FeatureType>(new OlFeature({
        ...properties,
        geometry,
        style
    }));
    const addFeatureListener = useAddFeatureListener();
    const removeFeatureListener = useRemoveFeatureListener();
    const contextLayer = useLayer();
    const _layer = layer || contextLayer;
    let _source = source || _layer && _layer.getSource();

    // Source is a Cluster. Get underlying source
    if(_source && _source.getSource) {
        _source = _source.getSource();
    }

    useEffect(() => {
        if(_source) {
            debug('Adding feature');

            _source.addFeature(feature);
        }

        return () => {
            if(_source) {
                debug('Removing feature');

                _source.removeFeature(feature);
            }
        };
    }, [_source, feature]);

    useEffect(() => {
        if(feature) {
            if(feature.getGeometry() !== geometry) {
                rtDebug('Setting geometry: ', geometry);
                feature.setGeometry(geometry);
            }

            // console.log('feature.getProperties(): ', feature.getProperties());
            // console.log('properties: ', properties);
            // if(feature.getProperties() !== properties) {
            //     rtDebug('Setting properties: ', properties);
            //     feature.setProperties(properties);
            //
            //     // feature.setProperties adds new properties and overwrites old one, but does not remove old ones. We have to do it manually
            //     const featureProperties = feature.getProperties();
            //     for(const key in featureProperties) {
            //         if(Object.prototype.hasOwnProperty.call(featureProperties, key)) {
            //             if(key !== 'geometry' && key !== 'style' && (!properties || typeof properties[key] === 'undefined') ) {
            //                 feature.unset(key);
            //             }
            //         }
            //     }
            // }
        }
    }, [feature, geometry, style, properties]);

    useEffect(() => {
        if(feature) {
            debug('Setting style: ', style);
            feature.setStyle(style);
        }
    }, [feature, style]);

    useEffect(() => {
        if(feature) {
            if(properties) {
                rtDebug('Setting properties: ', properties);
                feature.setProperties(properties);
            }

            // feature.setProperties adds new properties and overwrites old one, but does not remove old ones. We have to do it manually
            const featureProperties = feature.getProperties();
            for(const key in featureProperties) {
                if(Object.prototype.hasOwnProperty.call(featureProperties, key)) {
                    if(key !== 'geometry' && key !== 'style' && (!properties || typeof properties[key] === 'undefined') ) {
                        feature.unset(key);
                    }
                }
            }
        }
    }, [feature, properties]);

    useEffect(() => {
        if(feature && (onSelect || onDeselect)) {
            debug('Adding select listener to feature');
            addFeatureListener(feature, 'select', (selected) => {
                if(selected && onSelect) {
                    debug('Feature selected', feature);
                    onSelect();
                }
                else if(!selected && onDeselect) {
                    debug('Feature deselected', feature);
                    onDeselect();
                }
            });
        }

        return () => {
            if(feature && (onSelect || onDeselect)) {
                debug('Removing select listener from feature');
                removeFeatureListener(feature, 'select');
            }
        };
    }, [addFeatureListener, removeFeatureListener, feature, onSelect, onDeselect]);

    useEffect(() => {
        if(feature && onDragStart) {
            debug('Adding translateStart listener to feature');
            addFeatureListener(feature, 'translateStart', onDragStart);
        }

        return () => {
            if(feature && onDragStart) {
                debug('Removing translateStart listener from feature');
                removeFeatureListener(feature, 'translateStart');
            }
        };
    }, [addFeatureListener, removeFeatureListener, feature, onDragStart]);

    useEffect(() => {
        if(feature && onDrag) {
            debug('Adding translateStart listener to feature');
            addFeatureListener(feature, 'translating', onDrag);
        }

        return () => {
            if(feature && onDrag) {
                debug('Removing translateStart listener from feature');
                removeFeatureListener(feature, 'translating');
            }
        };
    }, [addFeatureListener, removeFeatureListener, feature, onDrag]);

    useEffect(() => {
        if(feature && onDragEnd) {
            debug('Adding translateStart listener to feature');
            addFeatureListener(feature, 'translateEnd', onDragEnd);
        }

        return () => {
            if(feature && onDragEnd) {
                debug('Removing translateStart listener from feature');
                removeFeatureListener(feature, 'translateEnd');
            }
        };
    }, [addFeatureListener, removeFeatureListener, feature, onDragEnd]);

    return null;
}

export default Feature;